// Runtime injection of per-client third-party scripts (Impel 360 viewer, RVD chatbot).
// Failures are logged and swallowed: the site must keep working when a vendor script
// is blocked or down.

export const findScriptBySrc = (src: string): HTMLScriptElement | null => {
  const scripts = document.head.querySelectorAll<HTMLScriptElement>('script[src]');
  return Array.from(scripts).find((script) => {
    try {
      const existing = new URL(script.src, window.location.origin).href;
      const target = new URL(src, window.location.origin).href;
      return existing === target;
    } catch {
      return false;
    }
  }) ?? null;
};

// Only http(s) script URLs are honored. Root-relative and protocol-relative values — the two
// shapes CuratedSiteConfig actually carries — resolve against the page origin and pass. `data:`
// is rejected because browsers DO execute it from a script src; other schemes are typos.
// This is defence in depth for an admin-controlled config value: it cannot stop an https URL
// that points at a hostile host, and is not a substitute for controlling who edits the config.
export const isHttpScriptUrl = (src: string): boolean => {
  if (!src?.trim()) {
    return false;
  }
  try {
    const {protocol} = new URL(src, window.location.origin);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

export interface InjectScriptOptions {
  // Called once the script has executed — and also when the script was already on the
  // page, so callers can (re)initialize regardless of who injected it first. If the
  // existing tag is still downloading, the call is deferred until it has loaded.
  onLoad?: () => void;
}

// Set on the tag once its load event fires. A load listener attached to an already-loaded
// tag never fires, so this is how a later caller tells "loaded" from "still downloading".
const LOADED_ATTRIBUTE = 'data-loaded';

export const injectScript = (src: string, options?: InjectScriptOptions): HTMLScriptElement | null => {
  if (!isHttpScriptUrl(src)) {
    console.error(`Refusing to inject script with a non-http(s) URL: ${src}`);
    return null;
  }

  const head = document.head;
  if (!head) {
    return null;
  }

  const existing = findScriptBySrc(src);
  if (existing) {
    if (options?.onLoad) {
      if (existing.hasAttribute(LOADED_ATTRIBUTE)) {
        options.onLoad();
      } else {
        existing.addEventListener('load', options.onLoad);
      }
    }
    return existing;
  }

  try {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => {
      script.setAttribute(LOADED_ATTRIBUTE, 'true');
    });
    if (options?.onLoad) {
      script.addEventListener('load', options.onLoad);
    }
    script.addEventListener('error', (err) => {
      console.error(`Failed to load script ${src}`, err);
    });
    head.appendChild(script);
    return script;
  } catch (err) {
    console.error(`Unable to inject script ${src}`, err);
    return null;
  }
};
